import { canUseDOM } from '@/utils/dom-utils';
import { S_USER_INFO } from '@/utils/web-storage-keys';

const AUTH_KEYS = {
  LOCAL_STORAGE_KEYS: [S_USER_INFO.keyData, 'S_USER_INFO'],
  COOKIE_KEYS: ['S_USER_SESSION', '7e2411adf07bfb27e6326b47734bead7'],
  TTL_MIN_LOCAL: 60 * 24 * 7,
};

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const decodeBase64Json = (raw) => {
  try {
    return JSON.parse(decodeURIComponent(atob(raw)));
  } catch {
    return null;
  }
};

const readRawLocalStorage = (keys) => {
  if (!canUseDOM()) return null;
  for (const key of keys) {
    const raw = localStorage.getItem(key);
    if (raw) return raw;
  }
  return null;
};

const readRawCookie = (keys) => {
  if (!canUseDOM() || !document.cookie) return null;
  for (const key of keys) {
    const match = document.cookie.match(new RegExp(`(?:^|; )${escapeRegExp(key)}=([^;]*)`));
    if (match) return decodeURIComponent(match[1].trim());
  }
  return null;
};

const isStorageExpired = (updateTime, expireMinutes) => {
  return !updateTime || !expireMinutes || Date.now() - updateTime > expireMinutes * 60 * 1000;
};

const isTokenExpired = (expiresAt) => {
  if (!expiresAt || expiresAt <= 0) return false;
  const ms = expiresAt > 1e12 ? expiresAt : expiresAt * 1000;
  return ms < Date.now();
};

const normalizeStoredData = (parsed) => {
  if (!parsed) return null;
  return parsed.data || parsed;
};

const readStoredAuth = ({ raw, source, ttlMinutes }) => {
  if (!raw) return null;
  const parsed = decodeBase64Json(raw);
  const data = normalizeStoredData(parsed);
  const storageExpired = source !== 'cookie' && isStorageExpired(parsed?.updateTime, ttlMinutes);

  if (!data?.token || storageExpired || isTokenExpired(data.expiresAt)) {
    return null;
  }

  return data;
};

export const readMyAccountAuth = () => {
  const fromLocal = readStoredAuth({
    raw: readRawLocalStorage(AUTH_KEYS.LOCAL_STORAGE_KEYS),
    source: 'localStorage',
    ttlMinutes: AUTH_KEYS.TTL_MIN_LOCAL,
  });
  if (fromLocal) {
    return {
      isLoggedIn: true,
      source: 'localStorage',
      email: fromLocal.email,
      token: fromLocal.token,
      profile: fromLocal.profile ?? null,
      user: fromLocal,
    };
  }

  const fromCookie = readStoredAuth({
    raw: readRawCookie(AUTH_KEYS.COOKIE_KEYS),
    source: 'cookie',
  });
  if (fromCookie) {
    return {
      isLoggedIn: true,
      source: 'cookie',
      email: fromCookie.email,
      token: fromCookie.token,
      profile: fromCookie.profile ?? null,
      user: fromCookie,
    };
  }

  return { isLoggedIn: false, source: null };
};

export default { readMyAccountAuth };
